import React, { useState } from 'react';

/// Web mirror of iOS LodgingStrip. Renders one passive card per hotel
/// covering this sheet's date. Data shape matches `sheet.hotels[]` from
/// the server projection in routes/sheets.js / routes/viewer.js. Hotels
/// are NOT timeline events; this is the only place they surface in the
/// admin sheet view.
export default function LodgingPanel({ hotels }) {
  if (!hotels || hotels.length === 0) return null;
  return (
    <div style={{ background:'var(--card)', borderRadius:16, border:'1px solid var(--border)', overflow:'hidden', marginBottom:16, marginTop:16 }}>
      <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid var(--border)' }}>
        <p style={{ fontSize:11, fontWeight:700, letterSpacing:'1.5px', color:'var(--text3)', margin:0 }}>LODGING</p>
      </div>
      <div style={{ padding:'10px 12px' }}>
        {hotels.map(h => <LodgingRow key={h.id} stay={h} />)}
      </div>
    </div>
  );
}

function statusLabel(stay) {
  if (stay.isCheckIn) return 'Check-in today';
  if (stay.isCheckOut) return 'Check-out today';
  if ((stay.totalNights || 1) > 1) return `Night ${stay.nightIndex} of ${stay.totalNights}`;
  return 'Lodging';
}

function prettyDate(ymd) {
  if (!ymd || ymd.length < 10) return '—';
  return new Date(ymd.slice(0, 10) + 'T12:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' });
}

function LodgingRow({ stay }) {
  const [expanded, setExpanded] = useState(false);
  const accent = '#06B6D4';
  const mapsHref = stay.hotelAddress ? `https://maps.apple.com/?q=${encodeURIComponent(stay.hotelAddress)}` : null;

  return (
    <div style={{
      background:'rgba(6,182,212,0.06)',
      border:'1px solid rgba(6,182,212,0.2)',
      borderRadius:12,
      padding:'12px 14px',
      marginBottom:8,
      cursor:'pointer',
    }} onClick={() => setExpanded(v => !v)}>
      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
        <div style={{
          width:32, height:32, borderRadius:8,
          background:'rgba(6,182,212,0.15)',
          display:'flex', alignItems:'center', justifyContent:'center',
        }}>
          <i className="fa-solid fa-bed" style={{ fontSize:14, color:accent }} />
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <p style={{ fontSize:10, fontWeight:800, letterSpacing:'0.6px', color:accent, margin:0, textTransform:'uppercase' }}>
            {statusLabel(stay)}
          </p>
          <p style={{ fontSize:15, fontWeight:800, color:'var(--text)', margin:'1px 0 0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {stay.hotelName || 'Hotel'}
          </p>
        </div>
        <i className={`fa-solid ${expanded ? 'fa-chevron-up' : 'fa-chevron-down'}`} style={{ fontSize:11, color:'var(--text3)' }} />
      </div>
      {stay.hotelAddress && (
        <p style={{
          fontSize:12, color:'var(--text2)', margin:'8px 0 0',
          overflow: expanded ? 'visible' : 'hidden',
          textOverflow: expanded ? 'clip' : 'ellipsis',
          whiteSpace: expanded ? 'normal' : 'nowrap',
        }}>
          <i className="fa-solid fa-location-dot" style={{ fontSize:10, marginRight:4, color:'var(--text3)' }} />
          {stay.hotelAddress}
        </p>
      )}
      {expanded && (
        <div style={{ borderTop:'1px solid rgba(6,182,212,0.15)', marginTop:10, paddingTop:10, display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 14px' }}>
          <Detail label="Check-in" value={prettyDate(stay.checkIn)} />
          <Detail label="Check-out" value={prettyDate(stay.checkOut)} />
          {stay.hotelConfirmation && <Detail label="Confirmation" value={stay.hotelConfirmation} />}
          {stay.roomType && <Detail label="Room" value={stay.roomType} />}
          {mapsHref && (
            <a href={mapsHref} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
              style={{ gridColumn:'1 / -1', fontSize:12, fontWeight:700, color:accent, textDecoration:'none', marginTop:2 }}>
              <i className="fa-solid fa-map" style={{ marginRight:6 }} /> Open in Maps
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p style={{ fontSize:9, fontWeight:800, letterSpacing:'0.5px', color:'var(--text3)', margin:0, textTransform:'uppercase' }}>{label}</p>
      <p style={{ fontSize:13, fontWeight:600, color:'var(--text)', margin:'1px 0 0' }}>{value}</p>
    </div>
  );
}
